import Vue from 'vue';
import Router from 'vue-router';

import store from '../store';

import Login from '../views/Login.vue';
import AnnouncementConfirm from '../views/AnnouncementConfirm.vue';
import MainLayout from '../layout/MainLayout.vue';

// Route-level lazy loading to reduce initial bundle size
const Dashboard = () => import(/* webpackChunkName: "dashboard" */ '../views/Dashboard.vue');
const UserAnnouncements = () => import(/* webpackChunkName: "common" */ '../views/UserAnnouncements.vue');
const TicketList = () => import(/* webpackChunkName: "tickets" */ '../views/tickets/TicketList.vue');
const TicketNewRoute = () => import(/* webpackChunkName: "tickets" */ '../views/tickets/TicketNewRoute.vue');
const TicketDetailRoute = () => import(/* webpackChunkName: "tickets" */ '../views/tickets/TicketDetailRoute.vue');
const TaskList = () => import(/* webpackChunkName: "tasks" */ '../views/tasks/TaskList.vue');
const HandlerPool = () => import(/* webpackChunkName: "handler" */ '../views/handler/HandlerPool.vue');
const HandlerInProgress = () => import(/* webpackChunkName: "handler" */ '../views/handler/HandlerInProgress.vue');
const HandlerClosed = () => import(/* webpackChunkName: "handler" */ '../views/handler/HandlerClosed.vue');
const HandlerCaseDetailRoute = () => import(/* webpackChunkName: "handler" */ '../views/handler/HandlerCaseDetailRoute.vue');
const HandlerDisputes = () => import(/* webpackChunkName: "handler" */ '../views/handler/HandlerDisputes.vue');
const KpiDashboard = () => import(/* webpackChunkName: "admin" */ '../views/kpi/KpiDashboard.vue');
const SecondaryPending = () => import(/* webpackChunkName: "secondary" */ '../views/secondary/SecondaryPending.vue');
const SecondarySubmit = () => import(/* webpackChunkName: "secondary" */ '../views/secondary/SecondarySubmit.vue');
const SecondaryClosed = () => import(/* webpackChunkName: "secondary" */ '../views/secondary/SecondaryClosed.vue');
const SecondaryCaseDetailRoute = () => import(/* webpackChunkName: "secondary" */ '../views/secondary/SecondaryCaseDetailRoute.vue');
const SecondaryTaskList = () => import(/* webpackChunkName: "secondary" */ '../views/secondary/SecondaryTaskList.vue');
const SecondaryAnnouncements = () => import(/* webpackChunkName: "secondary" */ '../views/secondary/SecondaryAnnouncements.vue');
const AdminUsers = () => import(/* webpackChunkName: "admin" */ '../views/admin/AdminUsers.vue');
const AdminTasks = () => import(/* webpackChunkName: "admin" */ '../views/admin/AdminTasks.vue');
const AdminAnnouncements = () => import(/* webpackChunkName: "admin" */ '../views/admin/AdminAnnouncements.vue');
const AdminSystem = () => import(/* webpackChunkName: "admin" */ '../views/admin/AdminSystem.vue');
const ChangePassword = () => import(/* webpackChunkName: "common" */ '../views/ChangePassword.vue');

Vue.use(Router);

const router = new Router({
  mode: 'hash',
  routes: [
    { path: '/login', component: Login, meta: { public: true } },
    { path: '/announcements/confirm', component: AnnouncementConfirm },
    {
      path: '/',
      component: MainLayout,
      redirect: '/dashboard',
      children: [
        { path: '/dashboard', component: Dashboard },
        { path: '/user/announcements', component: UserAnnouncements, meta: { roles: ['USER'] } },
        { path: '/tickets', component: TicketList, meta: { roles: ['USER'] } },
        { path: '/tickets/new', component: TicketNewRoute, meta: { roles: ['USER'] } },
        { path: '/tickets/:caseNo', component: TicketDetailRoute, meta: { roles: ['USER'] } },
        { path: '/tasks', component: TaskList, meta: { roles: ['USER'] } },
        { path: '/handler/pool', component: HandlerPool, meta: { roles: ['HANDLER'] } },
        { path: '/handler/in-progress', component: HandlerInProgress, meta: { roles: ['HANDLER'] } },
        { path: '/handler/closed', component: HandlerClosed, meta: { roles: ['HANDLER'] } },
        { path: '/handler/disputes', component: HandlerDisputes, meta: { roles: ['HANDLER'] } },
        { path: '/handler/cases/:caseNo', component: HandlerCaseDetailRoute, meta: { roles: ['HANDLER'] } },
        { path: '/secondary/submit', component: SecondarySubmit, meta: { roles: ['SECONDARY_HANDLER'] } },
        { path: '/secondary/pending', component: SecondaryPending, meta: { roles: ['SECONDARY_HANDLER'] } },
        { path: '/secondary/returned', redirect: '/secondary/pending', meta: { roles: ['SECONDARY_HANDLER'] } },
        { path: '/secondary/closed', component: SecondaryClosed, meta: { roles: ['SECONDARY_HANDLER'] } },
        { path: '/secondary/disputes', redirect: '/secondary/pending', meta: { roles: ['SECONDARY_HANDLER'] } },
        { path: '/secondary/cases/:caseNo', component: SecondaryCaseDetailRoute, meta: { roles: ['SECONDARY_HANDLER'] } },
        { path: '/secondary/tasks', component: SecondaryTaskList, meta: { roles: ['SECONDARY_HANDLER'] } },
        { path: '/secondary/announcements', component: SecondaryAnnouncements, meta: { roles: ['SECONDARY_HANDLER'] } },
        { path: '/kpi', component: KpiDashboard, meta: { roles: ['ADMIN'] } },
        { path: '/admin/users', component: AdminUsers, meta: { roles: ['ADMIN'] } },
        { path: '/admin/tasks', component: AdminTasks, meta: { roles: ['ADMIN'] } },
        { path: '/admin/announcements', component: AdminAnnouncements, meta: { roles: ['ADMIN'] } },
        { path: '/admin/system', component: AdminSystem, meta: { roles: ['ADMIN'] } },
        { path: '/change-password', component: ChangePassword }
      ]
    }
  ]
});

router.beforeEach(async (to, from, next) => {
  const isPublic = !!to.meta.public;

  // Cookie-based auth: we don't know auth state until we call /api/me.
  if (!store.state.user) {
    try {
      await store.dispatch('bootstrap');
    } catch (e) {
      // handled by axios interceptor
    }
  }

  // If user is already logged in, keep them out of /login.
  if (to.path === '/login' && store.state.user) {
    return next('/dashboard');
  }

  if (!isPublic && !store.state.user && to.path !== '/login') {
    return next('/login');
  }

  if (store.state.user && to.path !== '/announcements/confirm' && store.state.mustConfirmAnnouncement) {
    store.commit('setAnnouncementReturnTo', to.fullPath);
    return next('/announcements/confirm');
  }

  const roles = to.meta.roles || null;
  if (roles && store.state.user && roles.indexOf(store.state.user.role) === -1) {
    return next('/dashboard');
  }

  return next();
});

export default router;
